/**
 * Text helpers for rendering chat message bodies that mix plain text with
 * inline HTML.
 *
 * Two producers reach this path:
 *   - operator / bot messages from the dashboard TipTap editor — full HTML
 *     wrapped in `<p class="tiptap-paragraph">…`.
 *   - AI replies — plain text with `\n` line breaks and embedded fragments
 *     (`<a>`, `<ul>/<li>`, `<pre>`, …) mid-text; NOT wrapped in a block tag.
 *
 * The old renderer only treated a message as HTML when it STARTED with a tag,
 * so the AI's mid-text fragments showed as literal `<ul>` / `<a>` text. These
 * helpers detect HTML anywhere and keep the plain-text line breaks; the actual
 * markup is turned into a React tree by `parseArticleHtml` (html-react-parser)
 * — the same parser the article / operator HTML surfaces use.
 */

// The block-level tags whose surrounding newlines we trim (below). Kept in one
// place so `normalizeMessageHtml` and its tests stay in sync.
const BLOCK_TAGS = "ul|ol|li|pre|p|blockquote|h[1-6]";

/** True when the text contains at least one HTML tag ANYWHERE (not only at the
 *  start). AI replies embed tags mid-text, which the old leading-tag check
 *  missed. */
export function containsHtml(text: string): boolean {
  return /<[a-z][a-z0-9]*(\s|>|\/)/i.test(text);
}

// Markdown syntax signals — any one is enough to route a NON-HTML AI reply
// through the markdown renderer instead of showing raw `**`/`[]()`/`-` text.
const MARKDOWN_PATTERNS: RegExp[] = [
  /\*\*[^*\n]+\*\*/, // **bold**
  /__[^_\n]+__/, // __bold__
  /\[[^\]\n]+\]\([^)\n]+\)/, // [text](url)
  /`[^`\n]+`/, // `code`
  /```/, // ``` fenced code
  /^\s{0,3}#{1,6}\s+/m, // # heading
  /^\s*[-*+]\s+\S/m, // - list item
  /^\s*\d+\.\s+\S/m, // 1. ordered item
  /^\s*>\s+\S/m, // > blockquote
];

/**
 * Heuristic: does this NON-HTML text look like Markdown? The AI usually sends
 * HTML but sometimes emits Markdown (`**bold**`, `[text](url)`, `-` lists) — we
 * render those through `react-markdown` into the SAME styled container as the
 * HTML path so the two look identical, instead of showing raw syntax. Gated to
 * AI/operator (outbound) messages by the caller so a visitor's literal `**`
 * stays plain text. Only meaningful when `containsHtml` is false.
 */
export function looksLikeMarkdown(text: string): boolean {
  return MARKDOWN_PATTERNS.some((re) => re.test(text));
}

/**
 * Collapse the newlines that sit directly around block-level tags so a
 * `white-space: pre-wrap` container doesn't add blank lines inside/around
 * lists, code blocks, and paragraphs. Block elements already break onto their
 * own line, so those newlines are redundant. Newlines inside plain-text runs
 * (and inside `<pre>` content, which isn't adjacent to a tag) are preserved so
 * they render as real line breaks.
 */
export function normalizeMessageHtml(text: string): string {
  return (
    text
      .replace(new RegExp(`\\n+(?=</?(?:${BLOCK_TAGS})[\\s/>])`, "gi"), "")
      .replace(new RegExp(`(</?(?:${BLOCK_TAGS})[^>]*>)\\n+`, "gi"), "$1")
      // Strip stray <br> that sit DIRECTLY against a block boundary. Some AI /
      // editor output wraps block elements in line breaks (`</p><br><ul>`,
      // `<ul><br><li>`, `</li><br><li>`, `</li><br></ul>`), which render as empty
      // gap lines / broken lists. A <br> in the MIDDLE of text (a real
      // in-paragraph / in-item line break) isn't adjacent to a tag, so it's kept.
      .replace(new RegExp(`<br\\s*/?>(?=\\s*</?(?:${BLOCK_TAGS})[\\s/>])`, "gi"), "")
      .replace(new RegExp(`(</?(?:${BLOCK_TAGS})[^>]*>)\\s*<br\\s*/?>`, "gi"), "$1")
  );
}
